export type TweetLengthOption = 'short' | 'medium' | 'long';
export type TweetToneOption = 'one_liner' | 'explanatory' | 'story';
export type TweetStyleOption = 'assertive' | 'empathetic' | 'warning' | 'secret';

export interface TweetSettingsValues {
  length: TweetLengthOption;
  tone: TweetToneOption;
  style: TweetStyleOption;
  humanizeLevel: number; // 1-5
}

export const TWEET_SETTINGS_KEY = 'bangerfy_settings_v4';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export const defaultTweetSettings: TweetSettingsValues = {
  length: 'medium',
  tone: 'explanatory',
  style: 'assertive',
  humanizeLevel: 3,
};

export const LENGTH_OPTIONS: { value: TweetLengthOption; label: string; helper: string }[] = [
  { value: 'short', label: 'Short', helper: 'Up to 60 chars, straight to the point' },
  { value: 'medium', label: 'Medium', helper: '100-140 chars, quick read' },
  { value: 'long', label: 'Long', helper: '200+ chars, deep dive' },
];

export const TONE_OPTIONS: { value: TweetToneOption; label: string; helper: string }[] = [
  { value: 'one_liner', label: 'One-liner', helper: 'Sharp, punchy conclusion' },
  { value: 'explanatory', label: 'Explanatory', helper: 'Includes reasoning and context' },
  { value: 'story', label: 'Story', helper: 'Narrative arc with a beginning, middle, and end' },
];

export const STYLE_OPTIONS: { value: TweetStyleOption; label: string; helper: string }[] = [
  { value: 'assertive', label: 'Assertive', helper: 'Bold, decisive statements' },
  { value: 'empathetic', label: 'Empathetic', helper: 'Warm, relatable tone' },
  { value: 'warning', label: 'Warning', helper: 'Risk-focused, cautionary' },
  { value: 'secret', label: 'Insider', helper: 'Off-the-record, casual insider vibe' },
];

export function clampHumanizeLevel(level: number): number {
  const rounded = Math.round(level || 1);
  return clamp(rounded, 1, 5);
}

export function loadTweetSettingsFromStorage(): TweetSettingsValues {
  if (typeof localStorage === 'undefined') return defaultTweetSettings;

  try {
    const saved = localStorage.getItem(TWEET_SETTINGS_KEY);
    if (!saved) return defaultTweetSettings;
    const parsed = JSON.parse(saved);

    return {
      length: parsed.length || defaultTweetSettings.length,
      tone: parsed.tone || defaultTweetSettings.tone,
      style: parsed.style || defaultTweetSettings.style,
      humanizeLevel: clampHumanizeLevel(parsed.humanizeLevel ?? defaultTweetSettings.humanizeLevel),
    } satisfies TweetSettingsValues;
  } catch (error) {
    console.warn('[tweetSettings] Failed to load from storage', error);
    return defaultTweetSettings;
  }
}

export function saveTweetSettingsToStorage(settings: TweetSettingsValues): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      TWEET_SETTINGS_KEY,
      JSON.stringify({
        ...settings,
        humanizeLevel: clampHumanizeLevel(settings.humanizeLevel),
      })
    );
  } catch (error) {
    console.warn('[tweetSettings] Failed to save to storage', error);
  }
}

export function describeSettings(settings: TweetSettingsValues): {
  length: string;
  tone: string;
  style: string;
} {
  const lengthMap: Record<TweetLengthOption, string> = {
    short: 'Short (up to 60 chars)',
    medium: 'Medium (100-140 chars)',
    long: 'Long (200+ chars)',
  };

  const toneMap: Record<TweetToneOption, string> = {
    one_liner: 'One-liner (punchy single phrase)',
    explanatory: 'Explanatory (conclusion + reason + closing)',
    story: 'Story (narrative arc)',
  };

  const styleMap: Record<TweetStyleOption, string> = {
    assertive: 'Assertive (bold, decisive)',
    empathetic: 'Empathetic (warm, relatable)',
    warning: 'Warning (risk-focused, cautionary)',
    secret: 'Insider (off-the-record, casual)',
  };

  return {
    length: lengthMap[settings.length],
    tone: toneMap[settings.tone],
    style: styleMap[settings.style],
  };
}
